<template>
  <div class="layout-wrapper">
    <!-- 账户冻结警告横幅 - 明显且不可关闭 -->
    <div v-if="isFrozen" class="frozen-banner">
      <i class="el-icon-warning-outline"></i>
      <span class="frozen-text">{{ frozenMessage }}</span>
      <span class="frozen-contact">请联系686Pay管理员处理</span>
    </div>
    
    <!-- 顶部导航栏 -->
    <header class="top-header" :class="{ 'has-frozen-banner': isFrozen }">
      <div class="header-container">
        <!-- Logo -->
        <div class="logo" @click="handleLogoClick">
          <img src="/fhpay.jpg" alt="686Pay" class="logo-img" />
          <span class="logo-text">686Pay</span>
        </div>

        <!-- 主导航 -->
        <nav class="main-nav">
          <router-link 
            v-for="item in navItems" 
            :key="item.path"
            :to="item.path"
            :class="['nav-item', { active: isActive(item.path) }]"
          >
            {{ item.title }}
          </router-link>
        </nav>

        <!-- 右侧操作区 -->
        <div class="header-actions">
          <!-- 消息 -->
          <el-dropdown trigger="click" @visible-change="handleMessageVisible">
            <div class="action-btn">
              <el-badge :value="unreadCount" :hidden="unreadCount === 0" :max="99">
                <i class="el-icon-bell"></i>
              </el-badge>
            </div>
            <el-dropdown-menu slot="dropdown" class="message-menu">
              <div class="dropdown-header">
                <span>消息通知</span>
                <el-button v-if="unreadCount > 0" type="text" size="mini" @click="markAllRead">
                  全部已读
                </el-button>
              </div>
              <div class="message-list" v-if="messageList.length > 0">
                <div 
                  v-for="msg in messageList.slice(0, 5)" 
                  :key="msg.id"
                  class="message-item"
                  :class="{ unread: !msg.is_read }"
                  @click="readMessage(msg)"
                >
                  <div class="message-dot" :class="msg.type"></div>
                  <div class="message-info">
                    <div class="message-title">{{ msg.title }}</div>
                    <div class="message-time">{{ msg.created_at }}</div>
                  </div>
                </div>
              </div>
              <div class="message-empty" v-else>
                <i class="el-icon-chat-dot-round"></i>
                <p>暂无新消息</p>
              </div>
              <div class="dropdown-footer">
                <el-button type="text" @click="goToMessageCenter">
                  查看全部 <i class="el-icon-arrow-right"></i>
                </el-button>
              </div>
            </el-dropdown-menu>
          </el-dropdown>

          <!-- 用户菜单 -->
          <el-dropdown>
            <div class="user-menu">
              <div class="user-avatar">
                <el-avatar :size="36" :src="merchant.avatar">
                  <i class="el-icon-user-solid"></i>
                </el-avatar>
                <span class="online-badge"></span>
              </div>
              <div class="user-info">
                <span class="user-name">{{ merchant.company_name || '商户' }}</span>
                <span class="user-id">{{ merchant.merchant_no }}</span>
              </div>
              <i class="el-icon-arrow-down"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="$router.push('/account/index')">
                <i class="el-icon-user"></i> 账户中心
              </el-dropdown-item>
              <el-dropdown-item @click.native="$router.push('/account/index')">
                <i class="el-icon-lock"></i> 安全设置
              </el-dropdown-item>
              <el-dropdown-item @click.native="goToWebsite" divided>
                <i class="el-icon-house"></i> 返回官网
              </el-dropdown-item>
              <el-dropdown-item @click.native="logout">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </header>

    <!-- 子导航 -->
    <SubNav v-if="!isFrozen" />

    <!-- 主内容区 -->
    <main class="main-content" :class="{ 'has-subnav': showSubNav, 'has-frozen-banner': isFrozen }">
      <div class="content-wrapper">
        <router-view />
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SubNav from './SubNav'

export default {
  name: 'Layout',
  components: { SubNav },
  data() {
    return {
      allNavItems: [
        { path: '/merchant/dashboard', title: '首页', permission: 'can_view_dashboard' },
        { path: '/deposit', title: '收款', permission: 'can_view_deposit' },
        { path: '/payout', title: '付款', permission: 'can_view_payout' },
        { path: '/application', title: '进件', permission: 'can_view_application' },
        { path: '/settlement', title: '结算', permission: 'can_view_settlement' },
        { path: '/developer', title: '开发者', permission: 'can_view_developer' }
      ],
      unreadCount: 0,
      messageList: []
    }
  },
  computed: {
    ...mapGetters(['merchant', 'permissions']),
    navItems() {
      // 冻结商户只能看到首页
      if (this.isFrozen) {
        return this.allNavItems.filter(item => item.path === '/merchant/dashboard')
      }
      const userType = this.merchant?.user_type
      if (userType === 'merchant') return this.allNavItems
      return this.allNavItems.filter(item => {
        if (!item.permission) return true
        const value = this.permissions?.[item.permission]
        return value === true || value === 1 || value === '1'
      })
    },
    showSubNav() {
      return !this.$route.path.startsWith('/dashboard')
    },
    isFrozen() {
      return this.$store.state.user.isFrozen || this.merchant?.status === 2
    },
    frozenMessage() {
      return this.$store.state.user.frozenMessage || '您的账户已被冻结'
    }
  },
  mounted() {
    this.fetchUnreadCount()
    this.fetchMessages()
  },
  methods: {
    handleLogoClick() {
      // 如果已经在控制台页面，不重复导航
      if (this.$route.path === '/merchant/dashboard') {
        return
      }
      this.$router.push('/merchant/dashboard')
    },
    isActive(path) {
      if (path === '/merchant/dashboard') return this.$route.path === '/merchant/dashboard'
      return this.$route.path.startsWith(path)
    },
    logout() {
      this.$store.dispatch('user/logout')
      this.$router.push('/login')
    },
    goToWebsite() {
      window.open('/', '_blank')
    },
    handleMessageVisible(visible) {
      if (visible) this.fetchMessages()
    },
    async fetchUnreadCount() {
      try {
        const { getUnreadCount } = await import('@/api/message')
        const res = await getUnreadCount()
        this.unreadCount = res.count || 0
      } catch (e) {}
    },
    async fetchMessages() {
      try {
        const { getMessages } = await import('@/api/message')
        const res = await getMessages({ page: 1, page_size: 5 })
        this.messageList = res.items || []
      } catch (e) {}
    },
    async readMessage(msg) {
      if (!msg.is_read) {
        try {
          const { markAsRead } = await import('@/api/message')
          await markAsRead(msg.id)
          msg.is_read = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        } catch (e) {}
      }
      this.$alert(msg.content, msg.title, { confirmButtonText: '知道了' })
    },
    async markAllRead() {
      try {
        const { markAllAsRead } = await import('@/api/message')
        await markAllAsRead()
        this.messageList.forEach(m => m.is_read = true)
        this.unreadCount = 0
        this.$message.success('已全部标记为已读')
      } catch (e) {}
    },
    goToMessageCenter() {
      this.$router.push('/message')
    }
  }
}
</script>

<style lang="scss" scoped>
.layout-wrapper {
  min-height: 100vh;
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
}

// 冻结警告横幅
.frozen-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  height: 48px;
  background: linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  animation: frozenPulse 2s ease-in-out infinite;

  i {
    font-size: 18px;
    animation: frozenIconShake 1s ease-in-out infinite;
  }

  .frozen-text {
    font-weight: 600;
  }

  .frozen-contact {
    opacity: 0.9;
    font-size: 13px;
    padding-left: 12px;
    border-left: 1px solid rgba(255, 255, 255, 0.3);
  }
}

@keyframes frozenPulse {
  0%, 100% { box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3); }
  50% { box-shadow: 0 4px 20px rgba(220, 38, 38, 0.5); }
}

@keyframes frozenIconShake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
}

// 顶部导航 - 暗黑玻璃拟态
.top-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 72px;
  background: rgba(10, 10, 15, 0.85);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &.has-frozen-banner {
    top: 48px;
  }
}

.header-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

// Logo
.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;

  &:hover { opacity: 0.8; }
}

.logo-img {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  object-fit: cover;
}

.logo-text {
  font-size: 22px;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

// 主导航
.main-nav {
  display: flex;
  gap: 4px;
}

.nav-item {
  padding: 10px 20px;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  border-radius: 10px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.08);

    &::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 50%;
      transform: translateX(-50%);
      width: 16px;
      height: 3px;
      background: var(--gradient-primary);
      border-radius: 2px;
    }
  }
}

// 右侧操作区
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--text-primary);
  }

  i {
    font-size: 20px;
  }

  ::v-deep .el-badge__content {
    background: var(--danger-color);
    border: 2px solid #0a0a0f;
  }
}

// 用户菜单
.user-menu {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px 6px 6px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .el-icon-arrow-down {
    color: var(--text-muted);
    font-size: 12px;
  }
}

.user-avatar {
  position: relative;

  ::v-deep .el-avatar {
    background: linear-gradient(135deg, #333333 0%, #555555 100%);
  }
}

.online-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background: var(--success-color);
  border: 2px solid #0a0a0f;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.user-id {
  font-size: 12px;
  color: var(--text-muted);
  font-family: 'SF Mono', Monaco, 'Cascadia Mono', Consolas, monospace;
}

// 消息下拉菜单
.message-menu {
  width: 340px;
  padding: 0 !important;
  background: #1a1a1f;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-light);

  span {
    font-weight: 600;
    color: var(--text-primary);
  }
}

.message-list {
  max-height: 300px;
  overflow-y: auto;
}

.message-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid var(--border-light);

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &.unread {
    background: rgba(255, 255, 255, 0.03);

    .message-title {
      font-weight: 600;
      color: var(--text-primary);
    }
  }
}

.message-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.announcement { background: var(--danger-color); }
  &.merchant { background: var(--primary-color); }
}

.message-info {
  flex: 1;
  min-width: 0;
}

.message-title {
  font-size: 14px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-time {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 4px;
}

.message-empty {
  padding: 40px;
  text-align: center;
  color: var(--text-muted);

  i {
    font-size: 48px;
    color: var(--border-color);
    margin-bottom: 12px;
  }
}

.dropdown-footer {
  padding: 12px;
  text-align: center;
  border-top: 1px solid var(--border-light);
}

// 主内容区
.main-content {
  flex: 1;
  margin-top: 72px;
  padding: 24px 32px 40px;

  &.has-subnav {
    margin-top: 122px;
  }

  &.has-frozen-banner {
    margin-top: 120px;

    &.has-subnav {
      margin-top: 170px;
    }
  }
}

.content-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
